import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../Controllers/hooks";

import { Toast } from "primereact/toast";
import { Nullable } from "primereact/ts-helpers";
import { Ripple } from "primereact/ripple";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";
import { confirmDialog } from "primereact/confirmdialog";

import DocumentViewModal from "../../../../../Components/DocumentViewModal";
import FileInput from "../../../../../Components/FileInput";
import ErrorPage from "../../../../../Components/ErrorPage";
import DatePicker from "../../../../../Components/DatePicker";
import { goToTop } from "../../../../../Components/goToTop";
import { formatDate, parseStringToDate } from "../../../../../Utils/getDate";
import { checkFileOthers, checkText } from "../../../../../Utils/Validator";
import { getLoginUserRoleName, UserRoleName } from "../../../../../Utils/LoginUserRoleName";

import {
    InitialNotice as InitialNoticeType,
    InitialNoticeDocument
} from "../../../../../Controllers/Types/InitialNotice/InitialNoticeTypes";

import { useSelector } from "react-redux";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";
import { RootState } from "../../../../../Controllers/Store";
import { getInitialNoticeByFolderId } from "../../../../../Controllers/Thunks/BuildingInspectorThunks";
import { createInitialDocument, deleteInitialDocument, initialNoticeChangeStatus, initialNoticePublish } from "../../../../../Api/BuildingInspectorService/BuildingInspectorService";
import TableActionLoader from "../../../../../Components/TableActionLoader";
import PageLoader from "../../../../../Components/PageLoader";

const InitialNotice: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [loginUserRoleName, setLoginUserRoleName] = useState<UserRoleName | ''>('');
    const [initialDataLoading, setInitialDataLoading] = useState<boolean>(true);

    const projectData = location.state?.data;
    const folderId = Number(projectData.folder_id);

    const [initialNoticeData, setInitialNoticeData] = useState<InitialNoticeType | null>(null);
    const [isInitialNoticePublished, setIsInitialNoticePublished] = useState<boolean>(false);
    const [statuses] = useState<string[]>(['Pending', 'Accepted', 'Not Accepted']);

    const [detailsUpdateHasChanges, setDetailsUpdateHasChanges] = useState<boolean>(false);
    const [documentDeleteLoading, setDocumentDeleteLoading] = useState<boolean>(false);

    const [issuedDate, setIssuedDate] = useState<Nullable<Date>>(null);
    const [status, setStatus] = useState<'Pending' | 'Accepted' | 'Not Accepted' | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState<string>('');

    const [issuedDateError, setIssuedDateError] = useState<string>('');
    const [statusError, setStatusError] = useState<string>('');
    const [fileError, setFileError] = useState<string>('');

    const [selectedDocument, setSelectedDocument] = useState<InitialNoticeDocument | null>(null);

    const [showFileUploadModal, setShowFileUploadModal] = useState<boolean>(false);
    const [showFileViewModal, setShowFileViewModal] = useState(false);
    const [fileUrl, setFileUrl] = useState<string | null>(null);

    const [showNoticeUpdateModal, setShowNoticeUpdateModal] = useState<boolean>(false);

    const [isViewOnly, setIsViewOnly] = useState<boolean>(false);

    useEffect(() => {
        const fetchUserRole = async () => {
            const roleName = await getLoginUserRoleName();
            setLoginUserRoleName(roleName);
        };

        fetchUserRole();
    }, []);

    // PERMISION HANDLER
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    const hasPermissionStatusChange = useCanEdit("Initial Notice", "status_change");
    const hasPermissionUploadDocument = useCanEdit("Initial Notice", "upload_document");
    const hasPermissionDeleteDocument = useCanEdit("Initial Notice", "delete_document");
    const hasPermissionPublishNotice = useCanEdit("Initial Notice", "publish_notice");

    // for fetching data fro api tigger with state object actions
    useEffect(() => {
        initialNoticeDataGet();
    }, [folderId]);

    const initialNoticeDataGet = async () => {
        await dispatch(getInitialNoticeByFolderId(folderId));
        setInitialDataLoading(false);
    }

    // this is get data from state list and set projectlist
    const initalnotice = useAppSelector((state) => state?.initialNotice?.initialNoticeData);
    const loadingInitialNotice = useAppSelector((state) => state?.initialNotice?.loader);
    const errorInitialNotice = useAppSelector((state) => state?.initialNotice?.errorDisplay);

    useEffect(() => {
        if (initalnotice && initalnotice != null) {
            setInitialNoticeData(initalnotice);
        } else {
            setInitialNoticeData(null);
        }
    }, [initalnotice]);

    useEffect(() => {
        if (initialNoticeData && initialNoticeData !== null) {
            const isPublished: boolean = initialNoticeData?.is_published === 1 || false;

            setIsInitialNoticePublished(isPublished);
        }
    }, [initialNoticeData]);


    /* *************************** For notice detail ***************************** */
    useEffect(() => {
        if (initialNoticeData && initialNoticeData !== null) {
            if (formatDate(issuedDate!) !== initialNoticeData?.date) {
                setDetailsUpdateHasChanges(true);
            } else if (status !== initialNoticeData?.status) {
                setDetailsUpdateHasChanges(true);
            } else {
                setDetailsUpdateHasChanges(false);
            }
        }
    }, [initialNoticeData, issuedDate, status]);

    const onStatusChange = (e: DropdownChangeEvent) => {
        setStatus(e.value);
    };

    const statusOptionTemplate = (option: 'Pending' | 'Accepted' | 'Not Accepted') => {
        let colorCode = '';
        if (option === 'Pending') {
            colorCode = '#FFEFBC';
        } else if (option === 'Accepted') {
            colorCode = '#B9F6CA';
        } else if (option === 'Not Accepted') {
            colorCode = '#EF9A9A';
        }
        return (
            <div className="d-flex align-items-center w-100 justify-content-between">
                <div>{option}</div>
                <div className="color_display" style={{ backgroundColor: colorCode }}></div>
            </div>
        );
    };

    const getColorCode = (status: 'Pending' | 'Accepted' | 'Not Accepted'): string => {
        switch (status) {
            case 'Pending':
                return '#de9725';
            case 'Accepted':
                return '#06b500';
            case 'Not Accepted':
                return '#f60000';
            default:
                return '#000';
        }
    };

    const statusLabel = () => {
        const status = initialNoticeData?.status;
        let color = "";
        let bgColor = "";

        if (status === 'Pending') {
            color = "#de9725";
            bgColor = "#f8f0d6";
        } else if (status === 'Accepted') {
            color = "#06b500";
            bgColor = "#d5f8df";
        } else if (status === 'Not Accepted') {
            color = "#f60000";
            bgColor = "#fee2e2";
        }

        return (
            <span className="priority_label lg" style={{ color: color, backgroundColor: bgColor, width: '115px' }}>
                {status}
            </span>
        );
    };

    const handleCloseNoticeUpdateModal = () => {
        setShowNoticeUpdateModal(false);
        handleResetNoticeUpdateFields();
    }

    const handleResetNoticeUpdateFields = () => {
        setStatus(null);
        setIssuedDate(null);

        setStatusError('');
        setIssuedDateError('');
    }

    const handleAddInitialNoticeDetail = () => {
        if (initialNoticeData && initialNoticeData !== null) {
            setDataState('Add');
            setShowNoticeUpdateModal(true);
        }
    }

    const handleEditInitialNoticeDetail = () => {
        if (initialNoticeData && initialNoticeData !== null) {
            setDataState('Edit');
            setShowNoticeUpdateModal(true);

            setStatus(initialNoticeData?.status);
            setIssuedDate(parseStringToDate(initialNoticeData?.date!));
        }
    }

    function checkCredentialForNoticeDetail(): boolean {
        const isValidIssuedDate = checkText(formatDate(issuedDate!), true, setIssuedDateError);
        const isValidStatus = checkText(status!, true, setStatusError);

        return isValidIssuedDate && isValidStatus;
    }

    function showAPIErrorNoticeDetail(keyName: string, error: string): void {
        if (keyName === "status") {
            setStatusError(error);
        }

        if (keyName === "date") {
            setIssuedDateError(error);
        }
    }

    /* Clear validation message */
    useEffect(() => {
        checkText(status!, false, setStatusError);
    }, [status]);

    useEffect(() => {
        checkText(formatDate(issuedDate!), false, setIssuedDateError);
    }, [issuedDate]);


    const handleUpdateInitialNoticeDetail = async (e: React.FormEvent) => {
        e.preventDefault();
        if (checkCredentialForNoticeDetail()) {
            setLoading(true);
            const data = await initialNoticeChangeStatus(
                initialNoticeData?.id!,
                status!,
                formatDate(issuedDate!)
            );

            setLoading(false);

            if (data.statusCode === 200) {
                initialNoticeDataGet();
                handleCloseNoticeUpdateModal();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Details ${dataState === 'Add' ? 'added' : 'updated'} successfully.`,
                        life: 3000,
                    });
                }
            } else if (data.statusCode === 422) {
                // database error
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorNoticeDetail(key, value[0])
                );
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: `There was an error ${dataState === 'Add' ? 'adding' : 'updating'} the details.`,
                        life: 3000,
                    });
                }
            }
        }
    }

    /* Modal header */
    const noticeUpdateModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState}&nbsp;initial notice details
            </h5>

            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseNoticeUpdateModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    /* Modal footer */
    const noticeUpdateModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseNoticeUpdateModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Submit' : 'Update'}
                onClick={handleUpdateInitialNoticeDetail}
                loading={loading} className="custom_btn primary"
                disabled={dataState === 'Edit' && !detailsUpdateHasChanges}
            />
        </div>
    );
    /* *************************************************************************** */


    /* ****************************** For document ******************************** */
    const handleFileUpload = (file: File) => {
        setFile(file);
        setFileName(file.name);
    };

    const handleClearFile = () => {
        setFile(null);
        setFileName('');
    };

    const handleCloseFileUploadModal = () => {
        setShowFileUploadModal(false);
        handleResetFileUploadFields();
    }

    const handleResetFileUploadFields = () => {
        setFile(null);
        setFileName('');
        setSelectedDocument(null);

        setFileError('');
    }

    const handleUploadDocument = (document: InitialNoticeDocument) => {
        if (document) {
            setSelectedDocument(document);

            setDataState('Add');
            setShowFileUploadModal(true);
        }
    }

    const handleChangeDocument = () => {
        if (selectedDocument && selectedDocument !== null) {
            setDataState('Edit');
            setShowFileUploadModal(true);

            handleCloseDocumentViewModal();
        }
    }

    function checkCredentialForDocumentUpload(): boolean {
        const isDocumentFileValid = checkFileOthers(file!, true, setFileError);
        return isDocumentFileValid;
    }

    function showAPIErrorDocumentUpload(keyName: string, error: string): void {
        if (keyName === "document") {
            setFileError(error);
        }
    }

    /* Clear validation message */
    useEffect(() => {
        checkFileOthers(file!, false, setFileError);
    }, [file]);

    // upload document databse functions
    const uploadDocument = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentialForDocumentUpload()) {
            setLoading(true);

            const data = await createInitialDocument(
                selectedDocument?.id!,
                file!
            );

            setLoading(false);

            if (data.statusCode === 200) {
                initialNoticeDataGet();
                handleCloseFileUploadModal();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "Document uploaded successfully.",
                        life: 3000,
                    });
                }
            } else if (data.statusCode === 422) {
                // database error
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorDocumentUpload(key, value[0])
                );
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error uploading the document.",
                        life: 3000,
                    });
                }
            }
        }
    }

    /* Modal header */
    const documentUploadModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState === 'Add' ? 'Upload' : 'Edit'}&nbsp;document
            </h5>

            {dataState === "Edit" ? (
                <React.Fragment>
                    {documentDeleteLoading ? (
                        <TableActionLoader className="is_modal_loader" />
                    ) : (
                        <button
                            type="button"
                            className="close_modal_btn p-ripple"
                            onClick={() => handleDeleteDocument(selectedDocument?.id!)}>
                            <i className="bi bi-trash3"></i>
                            <Ripple />
                        </button>
                    )}
                </React.Fragment>
            ) : (
                <button
                    type="button"
                    className="close_modal_btn p-ripple"
                    onClick={handleCloseFileUploadModal}>
                    <i className="bi bi-x-circle"></i>
                    <Ripple />
                </button>
            )}
        </div>
    );

    /* Modal footer */
    const documentUploadModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseFileUploadModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Submit' : 'Update'}
                onClick={uploadDocument}
                loading={loading} className="custom_btn primary"
            />
        </div>
    );

    const handleDeleteDocument = (documentId: number) => {
        confirmDialog({
            message: (
                <span>
                    Are you sure you want to delete the <b className="text-capitalize">{selectedDocument?.document_name}</b> document?<br />
                </span>
            ),
            header: "Confirm the deletion",
            icon: "bi bi-info-circle",
            defaultFocus: "accept",
            acceptClassName: "p-button-danger",
            accept: () => deleteDocument(documentId),
            dismissableMask: true,
            resizable: false,
            draggable: false,
        });
    };

    const deleteDocument = async (documentId: number) => {
        setDocumentDeleteLoading(true);
        const result = await deleteInitialDocument(documentId);

        setDocumentDeleteLoading(false);

        if (result.statusCode === 200) {
            initialNoticeDataGet();
            handleCloseFileUploadModal();

            if (toast.current) {
                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Document deleted successfully.",
                    life: 2000,
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error deleting the document.",
                    life: 3000,
                });
            }
        }
    };
    /* *************************************************************************** */


    /* ****************************** For publish ******************************** */
    const handlePublishInitialNotice = () => {
        if (initialNoticeData && initialNoticeData !== null) {
            confirmDialog({
                message: 'Are you sure, Do you want to publish this initial notice?',
                header: 'Confirm the Publishion',
                icon: 'bi bi-info-circle',
                defaultFocus: 'accept',
                acceptClassName: 'p-button-success',
                accept: () => publishInitialNotice(initialNoticeData?.id),
                resizable: false,
                draggable: false,
                dismissableMask: true
            });
        }
    }

    const publishInitialNotice = async (initialNoticeId: number) => {
        const data = await initialNoticePublish(initialNoticeId);

        if (data.statusCode === 200) {
            initialNoticeDataGet();
            setSuccessMessage('The Initial Notice was successfully published.');
            setShowAlert(true);

            setTimeout(() => {
                setSuccessMessage('');
                setShowAlert(false);
            }, 3000);
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "An error occurred while publishing the initial notice.",
                    life: 3000,
                });
            }
        }
    }
    /* *************************************************************************** */


    /* ***************************** For view file ****************************** */
    const handleViewDocument = (document: InitialNoticeDocument) => {
        if (document) {
            setSelectedDocument(document);

            setFileUrl(document?.path);
            setShowFileViewModal(true);
        }
    }

    const handleCloseDocumentViewModal = () => {
        setShowFileViewModal(false);
        setFileUrl('');
    };
    /* *************************************************************************** */


    /* **************************** Common functions ***************************** */
    const handleCloseAlert = () => {
        setShowAlert(false);
    };

    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */


    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingInitialNotice && initialDataLoading ? (
                    <PageLoader />
                ) : errorInitialNotice ? (
                    <ErrorPage errorMessage={errorInitialNotice} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData.job_number} : {projectData.address}
                                </li>
                                <li className="page_breadcrumb_item has_child">
                                    {projectData.folder}
                                </li>
                                <li className="page_breadcrumb_item active">
                                    {projectData.feature_name}
                                </li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area mt-sm-3 mt-2">
                            <div className="page_header_text">
                                <h6>Initial Notice</h6>

                                {isInitialNoticePublished && (
                                    <React.Fragment>
                                        :{statusLabel()}
                                    </React.Fragment>
                                )}
                            </div>

                            {!isInitialNoticePublished && !isViewOnly && hasPermissionPublishNotice && (
                                <div className="page_action_area">
                                    <button
                                        className="add_data_link_btn p-ripple"
                                        onClick={handlePublishInitialNotice}
                                    >
                                        <i className="bi bi-send"></i>
                                        <span>Publish</span>
                                        <Ripple />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="page_main_content_section">
                            <div className="notice_data_area">
                                <div className="row notice_data_row">
                                    <div className="col-12 col-lg-6 notice_data_column">
                                        <article className="notice_data_container">
                                            <div className="notice_data_header">
                                                <h6 className="notice_data_head">
                                                    Note
                                                </h6>
                                            </div>

                                            <div className="notice_data_body">
                                                <p className="notice_data_note">
                                                    An initial notice notifies the Local Authority of the intended works and our intended building control oversight. It is important to carefully check the information in the document is correct and to ensure that the works are not started within five working days of the submission date.
                                                </p>

                                                {initialNoticeData && initialNoticeData !== null ? (
                                                    <div className="notice_detail_area">
                                                        {!isInitialNoticePublished && !isViewOnly && hasPermissionStatusChange && (
                                                            <button className="notice_detail_btn is_btn p-ripple"
                                                                onClick={() => {
                                                                    if (initialNoticeData?.date !== null && initialNoticeData?.status !== null) {
                                                                        handleEditInitialNoticeDetail();
                                                                    } else {
                                                                        handleAddInitialNoticeDetail();
                                                                    }
                                                                }}>
                                                                <i className="bi bi-pencil-square"></i>
                                                                <Ripple />
                                                            </button>
                                                        )}

                                                        <div className="notice_detail_field">
                                                            <label htmlFor="issuedDate">Issued Date:</label>
                                                            <h6>
                                                                {initialNoticeData?.date ? initialNoticeData?.date : '----'}
                                                            </h6>
                                                        </div>

                                                        <div className="notice_detail_field">
                                                            <label htmlFor="status">Status:</label>
                                                            <h6 style={{ color: getColorCode(initialNoticeData?.status!) }}>
                                                                {initialNoticeData?.status ? initialNoticeData?.status : '----'}
                                                            </h6>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="no_data_found mt-4">
                                                        <h6>No details found!</h6>
                                                    </div>
                                                )}
                                            </div>
                                        </article>
                                    </div>

                                    <div className="col-12 col-lg-6 notice_data_column">
                                        <article className="notice_data_container">
                                            <div className="notice_data_header">
                                                <h6 className="notice_data_head">Name</h6>
                                                <h6 className="notice_data_head pe-1">File</h6>
                                            </div>

                                            <div className="notice_data_body">
                                                {initialNoticeData && initialNoticeData?.initial_notice_document && initialNoticeData?.initial_notice_document?.length > 0 ? (
                                                    <div className="notice_files_area">
                                                        {initialNoticeData?.initial_notice_document?.map((document) => {
                                                            return (
                                                                <div key={document?.id} className="notice_file_field">
                                                                    <h6 className="notice_file_name">
                                                                        {document?.document_name}
                                                                    </h6>

                                                                    <Tooltip
                                                                        key={`upload_${document?.id}`}
                                                                        id={`upload_${document?.id}`}
                                                                        target={`.upload_${document?.id}`}
                                                                    />
                                                                    <Tooltip
                                                                        key={`view_${document?.id}`}
                                                                        id={`view_${document?.id}`}
                                                                        target={`.view_${document?.id}`}
                                                                    />

                                                                    {document?.path !== null ? (
                                                                        <button
                                                                            className={`data_action_btn with_bg danger_table_btn view_${document?.id} p-ripple`}
                                                                            onClick={() => handleViewDocument(document)}
                                                                            data-pr-tooltip={`View document`}
                                                                            data-pr-position="top"
                                                                        >
                                                                            <i className="bi bi-file-earmark-pdf"></i>
                                                                            <Ripple />
                                                                        </button>
                                                                    ) : (
                                                                        <React.Fragment>
                                                                            {!isInitialNoticePublished && !isViewOnly && hasPermissionUploadDocument ? (
                                                                                <button
                                                                                    className={`data_action_btn with_bg primary_table_btn upload_${document?.id} p-ripple`}
                                                                                    onClick={() => handleUploadDocument(document)}
                                                                                    data-pr-tooltip={`Upload document`}
                                                                                    data-pr-position="top"
                                                                                >
                                                                                    <i className="bi bi-upload"></i>
                                                                                    <Ripple />
                                                                                </button>
                                                                            ) : (
                                                                                <span className="no_data_text pe-2">N/A</span>
                                                                            )}
                                                                        </React.Fragment>
                                                                    )}
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                ) : (
                                                    <div className="no_data_found h-100">
                                                        <h6>No documents found!</h6>
                                                    </div>
                                                )}
                                            </div>
                                        </article>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>


            {/* Notice detail modal */}
            <Dialog
                visible={showNoticeUpdateModal}
                header={noticeUpdateModalHeader}
                footer={noticeUpdateModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseNoticeUpdateModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
                        {initialNoticeData && initialNoticeData !== null && (
                            <div className="row">
                                {/* Issued date */}
                                <div className="col-12">
                                    <DatePicker
                                        key={`issuedDate`}
                                        id="issuedDate"
                                        label="Issued Date"
                                        labelHtmlFor="issuedDate"
                                        required={true}
                                        value={issuedDate}
                                        placeholder="dd/mm/yyyy"
                                        onChange={setIssuedDate}
                                        error={issuedDateError}
                                        showClear
                                        onClear={() => setIssuedDate(null)}
                                    />
                                </div>

                                {/* Status */}
                                <div className="col-12">
                                    <div className="page_form_group mb-2">
                                        <label htmlFor="status" className="custom_form_label is_required">Status</label>
                                        <Dropdown
                                            id="status"
                                            value={status}
                                            onChange={onStatusChange}
                                            options={statuses}
                                            itemTemplate={statusOptionTemplate}
                                            panelClassName="status_option_panel"
                                            placeholder="Select status"
                                            className="form_dropdown w-100"
                                            showClear
                                        />

                                        {statusError &&
                                            <small className="form_error_msg">{statusError}</small>
                                        }
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Dialog >
            {/*  */}


            {/* File upload modal */}
            <Dialog
                visible={showFileUploadModal}
                header={documentUploadModalHeader}
                footer={documentUploadModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseFileUploadModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
                        {selectedDocument && selectedDocument !== null && (
                            <div className="row">
                                {/* Document name */}
                                <div className="col-12">
                                    <div className="page_form_group">
                                        <h6 className="data_label">
                                            {selectedDocument?.document_name}
                                        </h6>
                                    </div>
                                </div>

                                {/* File */}
                                <div className="col-12">
                                    <FileInput
                                        id="file"
                                        label="Upload file"
                                        labelHtmlFor="file"
                                        required={true}
                                        value={fileName}
                                        onUpload={handleFileUpload}
                                        onClear={handleClearFile}
                                        toast={toast}
                                        error={fileError}
                                        containerClassName="mb-sm-0"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Dialog >
            {/*  */}


            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showFileViewModal}
                onClose={handleCloseDocumentViewModal}
                fileUrl={fileUrl!}
                hasEdit={(!isInitialNoticePublished && !isViewOnly && hasPermissionUploadDocument) ? true : false}
                onEdit={handleChangeDocument}
            />
            {/*  */}


            {/* Show success message */}
            <div className={`message_backdrop ${showAlert ? 'show' : ''}`} onClick={handleCloseAlert}>
                <div className={`message_box ${showAlert ? 'show' : ''}`} onClick={e => e.stopPropagation()}>
                    <div className="message_box_content">
                        <i className="bi bi-check-circle-fill message_box_icon"></i>
                        <h6 className="message_box_message">
                            {successMessage}
                        </h6>
                    </div>
                </div>
            </div>
            {/*  */}
        </React.Fragment>
    )
}

export default InitialNotice;